import React, { useMemo } from "react";
import { useGetBiBrandActiveUsersQuery } from "../../app/api/console/consoleBiApiSlice";

/**
 * BI timestamps are UTC. ISO strings without a zone are parsed as *local* in JS;
 * append Z so the instant matches the server (fixes ~3h wall-clock errors in ET).
 */
function parseActivityTimestamp(ts) {
  if (!ts) return null;
  const s = String(ts).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[zZ]$|[+-]\d{2}:\d{2}/.test(s)) {
    const d = new Date(`${s}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** YYYY-MM-DD in America/New_York (same calendar as DAU/WAU on the server). */
function etYmd(date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Whole calendar days from activity (ET) to today (ET). */
function calendarDaysAgoEt(ts) {
  const d = parseActivityTimestamp(ts);
  if (!d) return null;
  const act = etYmd(d);
  const today = etYmd(new Date());
  const [y1, m1, d1] = act.split("-").map(Number);
  const [y2, m2, d2] = today.split("-").map(Number);
  const t1 = Date.UTC(y1, m1 - 1, d1);
  const t2 = Date.UTC(y2, m2 - 1, d2);
  return Math.round((t2 - t1) / (1000 * 60 * 60 * 24));
}

function formatRelativeEt(ts) {
  const days = calendarDaysAgoEt(ts);
  if (days === null) return "—";
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  const d = parseActivityTimestamp(ts);
  return d
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
      }).format(d)
    : "—";
}

/** Date/time in America/New_York; suffix "ET" (not EST/EDT) so DST changes don’t mix abbreviations. */
const easternTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function formatTimestampEst(ts) {
  const d = parseActivityTimestamp(ts);
  if (!d) return "";
  return `${easternTimeFormatter.format(d)} ET`;
}

/**
 * Unique users for this brand in the window with true last-activity time (full merge, no event-row limit).
 * Matches the cohort behind activity-trends DAU/WAU for the same brand + dates.
 */
export default function CustomerBrandActiveUsers({ brandName, enabled, startDate, endDate }) {
  const { data, isLoading, error } = useGetBiBrandActiveUsersQuery(
    {
      startDate,
      endDate,
      brands: brandName ? [brandName] : [],
      useProd: true,
    },
    { skip: !enabled || !brandName || !startDate || !endDate }
  );

  const rows = useMemo(() => {
    const users = data?.users;
    if (!users?.length) return [];
    return [...users].sort((a, b) => {
      const ta = parseActivityTimestamp(a.last_activity_at)?.getTime() ?? 0;
      const tb = parseActivityTimestamp(b.last_activity_at)?.getTime() ?? 0;
      return tb - ta;
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className='rounded-xl border border-border-subtle bg-white p-4 h-[340px] flex items-center justify-center'>
        <div className='flex flex-col items-center gap-2'>
          <div className='animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent' />
          <span className='text-caption text-text-tertiary'>Loading users…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-xl border border-border-subtle bg-white p-4 h-[340px] flex items-center justify-center'>
        <p className='text-body-xs text-severity-high text-center'>Couldn&apos;t load active users</p>
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-border-subtle bg-white flex flex-col h-[340px] overflow-hidden'>
      <div className='px-4 py-3 border-b border-border-subtle shrink-0'>
        <h4 className='text-body-xs font-semibold text-text-primary'>Who was active</h4>
        <p className='text-caption text-text-tertiary mt-0.5'>
          {rows.length} user{rows.length !== 1 ? "s" : ""}
          {rows.length > 0 ? " · Last activity (ET, same cohort as chart)" : ""}
        </p>
      </div>
      <div className='flex-1 overflow-y-auto px-2 py-2 min-h-0'>
        {rows.length === 0 ? (
          <p className='text-body-xs text-text-tertiary px-2 py-4 text-center'>No user activity in this window</p>
        ) : (
          <ul className='space-y-0.5'>
            {rows.map(({ username, last_activity_at: timestamp }) => (
              <li
                key={username}
                className='flex items-start justify-between gap-2 px-2 py-2 rounded-lg hover:bg-surface-neutral/80 text-body-xs'>
                <span className='font-medium text-text-primary truncate min-w-0' title={username}>
                  {username}
                </span>
                <div className='text-right shrink-0 min-w-0'>
                  <div className='text-text-secondary font-medium tabular-nums'>{formatRelativeEt(timestamp)}</div>
                  <div className='text-caption text-text-tertiary mt-0.5 tabular-nums leading-tight'>
                    {formatTimestampEst(timestamp) || "—"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
